// src/components/SmartCaptureModal.jsx
import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Mic, StopCircle, Upload } from "lucide-react";

export default function SmartCaptureModal({ show, onClose, onTextExtracted }) {
    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    if (!show) return null;

    // ───────────────────────────────────────────
    // AUDIO RECORDING
    // ───────────────────────────────────────────
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = evt => audioChunksRef.current.push(evt.data);
            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: "audio/mp3" });
                onTextExtracted({ type: "audio", audioBlob: blob });
                stream.getTracks().forEach(t => t.stop());
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
        } catch (err) {
            alert("Microphone access denied.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    // ───────────────────────────────────────────
    // FILE IMPORT (IMAGE OR AUDIO)
    // ───────────────────────────────────────────
    const handleFileImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type.startsWith("audio/")) {
            onTextExtracted({ type: "audio", audioBlob: file });
            return;
        }

        if (file.type.startsWith("image/") || file.type === "application/pdf") {
            const reader = new FileReader();
            reader.onloadend = () => {
                onTextExtracted({
                    type: "image",
                    imageBase64: reader.result,
                });
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <motion.div
                initial={{ opacity: 0, y: 80 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 80 }}
                className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-sm w-full border border-white/20 text-white shadow-2xl"
            >
                <h2 className="text-3xl font-bold text-center">Smart Capture</h2>
                <p className="text-center text-white/70 text-sm mb-6">Audio or Image/Text Input</p>

                {/* RECORDING */}
                <div className="flex justify-center mb-6">
                    <button
                        onClick={isRecording ? stopRecording : startRecording}
                        className="flex flex-col items-center gap-2"
                    >
                        <div className={`w-24 h-24 rounded-full flex items-center justify-center transition ${
                            isRecording ? "bg-red-500" : "bg-blue-500"
                        }`}>
                            {isRecording ? <StopCircle size={48} /> : <Mic size={48} />}
                        </div>
                        <span className="font-semibold">
                            {isRecording ? "Stop Recording" : "Start Recording"}
                        </span>
                    </button>
                </div>

                {/* FILE IMPORT */}
                <label className="flex flex-col items-center justify-center p-4 bg-white/10 rounded-xl cursor-pointer hover:bg-white/20 transition">
                    <Upload size={26} />
                    <span className="text-sm mt-1">Import Audio / Image</span>
                    <input type="file" accept="audio/*,image/*,application/pdf" className="hidden" onChange={handleFileImport} />
                </label>

                <button
                    className="mt-6 text-center w-full text-white/80 hover:text-white text-sm"
                    onClick={onClose}
                >
                    Cancel
                </button>
            </motion.div>
        </div>
    );
}
